/**
 * M5 `analysisDisplayConfigs/{id}` の `displayRules` 構造定義 (Sprint AU、Phase 5)。
 *
 * 旧 v1 `AnalysisDisplayConfig.displayRules` の sub-structure を踏襲しつつ、Phase 5 で
 * 動的軸・凡例・weight の完全マスター駆動化を支援。
 *
 * - 全 field optional (既存 master データが空 object / 部分実装の可能性に対応)
 * - `parseAnalysisDisplayRules(unknown)` で安全変換 (invalid 値は捨てて default 適用)
 *
 * 旧との差分:
 *   - matrix / map / heatmap / network のグルーピングを明示 (旧は flat だった field を整理)
 *   - colorRule の `colorAxis` を union literal 化 (旧 string)
 *   - postDetailFields は ECW Renderer 用 (旧 v1 踏襲)
 */
/** PostDetailField の canonical 表示順 (admin が未設定なら fallback で採用)。 */
export const DEFAULT_POST_DETAIL_FIELDS = [
    'description',
    'emotions',
    'emotionText',
    'emotionReason',
    'categoryCodes',
    'categoryReason',
];
/* ===== Parser / safe-fallback helpers ===== */
function isRecord(v) {
    return typeof v === 'object' && v !== null && !Array.isArray(v);
}
function pickString(v) {
    return typeof v === 'string' ? v : undefined;
}
function pickNumber(v) {
    return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}
function pickBoolean(v) {
    return typeof v === 'boolean' ? v : undefined;
}
function pickLiteral(v, allowed) {
    return typeof v === 'string' && allowed.includes(v) ? v : undefined;
}
function pickStringRecord(v) {
    if (!isRecord(v))
        return undefined;
    const out = {};
    for (const [k, val] of Object.entries(v)) {
        if (typeof val === 'string')
            out[k] = val;
    }
    return Object.keys(out).length > 0 ? out : undefined;
}
function parseAxisRule(v) {
    if (!isRecord(v))
        return undefined;
    const axisKey = pickString(v.axisKey);
    if (!axisKey)
        return undefined;
    const rule = { axisKey };
    const bucketMethod = pickLiteral(v.bucketMethod, [
        'auto',
        'by-name',
        'mean-split',
        'positive-negative',
        'custom-ranges',
    ]);
    if (bucketMethod)
        rule.bucketMethod = bucketMethod;
    if (Array.isArray(v.customRanges)) {
        const ranges = v.customRanges
            .map((r) => {
            if (!isRecord(r))
                return null;
            const label = pickString(r.label);
            const min = pickNumber(r.min);
            const max = pickNumber(r.max);
            if (label === undefined || min === undefined || max === undefined)
                return null;
            return { label, min, max };
        })
            .filter((r) => r !== null);
        if (ranges.length > 0)
            rule.customRanges = ranges;
    }
    if (v.includeEmpty === true)
        rule.includeEmpty = true;
    return rule;
}
function parseColorRule(v) {
    if (!isRecord(v))
        return undefined;
    const colorAxis = pickLiteral(v.colorAxis, [
        'majorCd',
        'categoryCd',
        'sentiment',
        'custom',
    ]);
    if (!colorAxis)
        return undefined;
    const rule = { colorAxis };
    const colorMap = pickStringRecord(v.colorMap);
    if (colorMap)
        rule.colorMap = colorMap;
    if (isRecord(v.sentimentGradient)) {
        const negative = pickString(v.sentimentGradient.negative);
        const neutral = pickString(v.sentimentGradient.neutral);
        const positive = pickString(v.sentimentGradient.positive);
        if (negative && neutral && positive) {
            rule.sentimentGradient = { negative, neutral, positive };
        }
    }
    const defaultColor = pickString(v.defaultColor);
    if (defaultColor)
        rule.defaultColor = defaultColor;
    return rule;
}
/**
 * 動的管理拡張: string (legacy 6 種) と object (PostDetailFieldRef) を両方受け入れる。
 * 不正値は drop、object で type が legacy なら sourceKey 無視、type='custom' で sourceKey が
 * 空なら drop (描画不可)。
 */
function parsePostDetailFields(v) {
    if (!Array.isArray(v))
        return undefined;
    const LEGACY = new Set(DEFAULT_POST_DETAIL_FIELDS);
    const out = [];
    for (const entry of v) {
        if (typeof entry === 'string') {
            if (LEGACY.has(entry))
                out.push(entry);
            continue;
        }
        if (!isRecord(entry))
            continue;
        const type = pickString(entry.type);
        if (!type)
            continue;
        if (type === 'custom') {
            const sourceKey = pickString(entry.sourceKey);
            if (!sourceKey)
                continue;
            const ref = { type: 'custom', sourceKey };
            const label = pickString(entry.label);
            if (label)
                ref.label = label;
            out.push(ref);
            continue;
        }
        if (LEGACY.has(type)) {
            const ref = { type: type };
            const label = pickString(entry.label);
            if (label)
                ref.label = label;
            const sourceKey = pickString(entry.sourceKey);
            if (sourceKey)
                ref.sourceKey = sourceKey;
            out.push(ref);
        }
    }
    return out.length > 0 ? out : undefined;
}
/**
 * docs/105 A-③: postDetailScores の safe-parse。
 * - 非 object / columns 非配列は undefined
 * - 各列は id / label / fieldPath (string) と calculation (kind 判別) を最低限要求、
 *   不正な列は drop。全列 drop なら undefined。
 * - `enabled` は boolean のみ (未指定は false 扱い)。
 */
function parseScoreCalculation(v) {
    if (!isRecord(v))
        return undefined;
    const kind = pickLiteral(v.kind, ['aggregate', 'formula']);
    if (kind === 'aggregate') {
        const aggregator = pickLiteral(v.aggregator, ['avg', 'sum', 'max', 'min', 'median']);
        if (!aggregator)
            return undefined;
        const out = { kind: 'aggregate', aggregator };
        const multiplier = pickNumber(v.multiplier);
        if (multiplier !== undefined)
            out.multiplier = multiplier;
        return out;
    }
    if (kind === 'formula') {
        const formula = pickString(v.formula);
        if (formula === undefined)
            return undefined;
        return { kind: 'formula', formula };
    }
    return undefined;
}
function parseScoreColumn(v) {
    if (!isRecord(v))
        return undefined;
    const id = pickString(v.id);
    const label = pickString(v.label);
    // fieldPath は formula 列では空文字でよいので string であれば許容 (undefined は ''+fallback)。
    const fieldPathRaw = pickString(v.fieldPath);
    if (id === undefined || label === undefined)
        return undefined;
    const calculation = parseScoreCalculation(v.calculation);
    if (!calculation)
        return undefined;
    const selectionMode = pickLiteral(v.selectionMode, ['all', 'first']) ?? 'all';
    const filterExpr = isRecord(v.filter) ? (pickString(v.filter.expr) ?? '') : '';
    const col = {
        id,
        label,
        fieldPath: fieldPathRaw ?? '',
        selectionMode,
        filter: { expr: filterExpr },
        calculation,
    };
    const decimals = pickNumber(v.decimals);
    if (decimals !== undefined)
        col.decimals = decimals;
    const unit = pickString(v.unit);
    if (unit !== undefined)
        col.unit = unit;
    return col;
}
function parseScoreDisplayConfig(v) {
    if (!isRecord(v))
        return undefined;
    if (!Array.isArray(v.columns))
        return undefined;
    const columns = v.columns
        .map(parseScoreColumn)
        .filter((c) => c !== undefined);
    if (columns.length === 0)
        return undefined;
    const enabled = pickBoolean(v.enabled) ?? false;
    return { enabled, columns };
}
/**
 * 任意の `unknown` 値を安全に `AnalysisDisplayRules` に変換 (Sprint AU)。
 *
 * - 入力が null / non-object / 配列の場合は空 object `{}` を返す
 * - 各 field は型が一致しない or 不正値 (例: 必須 sub-field 欠落) は無視
 * - 既存 master データの後方互換 (旧 `mapDefaults` `matrixDefaults` の flat 構造) は
 *   本 sprint では認識しない: admin の form で再保存すれば新構造になる。
 *   完全な migration が必要なら別 sprint で seed / migration script を追加。
 */
export function parseAnalysisDisplayRules(raw) {
    if (!isRecord(raw))
        return {};
    const out = {};
    /* matrix */
    if (isRecord(raw.matrix)) {
        const m = {};
        const defaultVAxis = pickString(raw.matrix.defaultVAxis);
        const defaultHAxis = pickString(raw.matrix.defaultHAxis);
        if (defaultVAxis)
            m.defaultVAxis = defaultVAxis;
        if (defaultHAxis)
            m.defaultHAxis = defaultHAxis;
        const vAxisRule = parseAxisRule(raw.matrix.vAxisRule);
        const hAxisRule = parseAxisRule(raw.matrix.hAxisRule);
        if (vAxisRule)
            m.vAxisRule = vAxisRule;
        if (hAxisRule)
            m.hAxisRule = hAxisRule;
        if (Array.isArray(raw.matrix.additionalAxisRules)) {
            const rules = raw.matrix.additionalAxisRules
                .map(parseAxisRule)
                .filter((r) => r !== undefined);
            if (rules.length > 0)
                m.additionalAxisRules = rules;
        }
        // MIG-1 M-A: scoreFilter (number[]、不正値は drop)。
        if (Array.isArray(raw.matrix.scoreFilter)) {
            const filter = raw.matrix.scoreFilter
                .map((v) => pickNumber(v))
                .filter((v) => v !== undefined);
            if (filter.length > 0)
                m.scoreFilter = filter;
        }
        // MIG-1 M-B: imageSize / showTitle / sortField / sortOrder。
        const imageSize = pickNumber(raw.matrix.imageSize);
        if (imageSize !== undefined)
            m.imageSize = imageSize;
        const showTitle = pickBoolean(raw.matrix.showTitle);
        if (showTitle !== undefined)
            m.showTitle = showTitle;
        const sortField = pickString(raw.matrix.sortField);
        if (sortField)
            m.sortField = sortField;
        const sortOrder = pickLiteral(raw.matrix.sortOrder, ['asc', 'desc']);
        if (sortOrder)
            m.sortOrder = sortOrder;
        const dataSource = pickLiteral(raw.matrix.dataSource, ['post', 'cls']);
        if (dataSource)
            m.dataSource = dataSource;
        // 軸セレクタから除外する axisKey (string[]、空文字は drop)。
        if (Array.isArray(raw.matrix.hiddenAxisKeys)) {
            const hidden = raw.matrix.hiddenAxisKeys
                .map((v) => pickString(v))
                .filter((v) => v !== undefined && v.length > 0);
            if (hidden.length > 0)
                m.hiddenAxisKeys = hidden;
        }
        if (Object.keys(m).length > 0)
            out.matrix = m;
    }
    /* map */
    if (isRecord(raw.map)) {
        const mp = {};
        if (isRecord(raw.map.defaultCenter)) {
            const lat = pickNumber(raw.map.defaultCenter.lat);
            const lng = pickNumber(raw.map.defaultCenter.lng);
            if (lat !== undefined && lng !== undefined)
                mp.defaultCenter = { lat, lng };
        }
        const defaultZoom = pickNumber(raw.map.defaultZoom);
        if (defaultZoom !== undefined)
            mp.defaultZoom = defaultZoom;
        const defaultColorAxis = pickString(raw.map.defaultColorAxis);
        if (defaultColorAxis)
            mp.defaultColorAxis = defaultColorAxis;
        const defaultSizeAxis = pickString(raw.map.defaultSizeAxis);
        if (defaultSizeAxis)
            mp.defaultSizeAxis = defaultSizeAxis;
        const defaultTileType = pickLiteral(raw.map.defaultTileType, ['osm', 'pmtiles']);
        if (defaultTileType)
            mp.defaultTileType = defaultTileType;
        const defaultMarkerMode = pickBoolean(raw.map.defaultMarkerMode);
        if (defaultMarkerMode !== undefined)
            mp.defaultMarkerMode = defaultMarkerMode;
        const defaultHeatmapMode = pickBoolean(raw.map.defaultHeatmapMode);
        if (defaultHeatmapMode !== undefined)
            mp.defaultHeatmapMode = defaultHeatmapMode;
        const colorRule = parseColorRule(raw.map.colorRule);
        if (colorRule)
            mp.colorRule = colorRule;
        if (Object.keys(mp).length > 0)
            out.map = mp;
    }
    /* heatmap */
    if (isRecord(raw.heatmap)) {
        const h = {};
        const defaultWeightMode = pickLiteral(raw.heatmap.defaultWeightMode, [
            'count',
            'score',
        ]);
        if (defaultWeightMode)
            h.defaultWeightMode = defaultWeightMode;
        const weightField = pickString(raw.heatmap.weightField);
        if (weightField)
            h.weightField = weightField;
        const weightAgg = pickLiteral(raw.heatmap.weightAgg, [
            'count',
            'sum',
            'avg',
            'product',
            'abs-product',
        ]);
        if (weightAgg)
            h.weightAgg = weightAgg;
        const defaultRadius = pickNumber(raw.heatmap.defaultRadius);
        if (defaultRadius !== undefined)
            h.defaultRadius = defaultRadius;
        const defaultIntensity = pickNumber(raw.heatmap.defaultIntensity);
        if (defaultIntensity !== undefined)
            h.defaultIntensity = defaultIntensity;
        const defaultOpacity = pickNumber(raw.heatmap.defaultOpacity);
        if (defaultOpacity !== undefined)
            h.defaultOpacity = defaultOpacity;
        const weightFormula = pickString(raw.heatmap.weightFormula);
        if (weightFormula && weightFormula.trim() !== '')
            h.weightFormula = weightFormula.trim();
        // reviewer follow-up ⚠️-5: secondaryWeightField を pick (未指定なら fallback で HealthScore)。
        const secondaryWeightField = pickString(raw.heatmap.secondaryWeightField);
        if (secondaryWeightField && secondaryWeightField.trim() !== '') {
            h.secondaryWeightField = secondaryWeightField.trim();
        }
        if (Object.keys(h).length > 0)
            out.heatmap = h;
    }
    /* score (Task D 動的化) */
    if (isRecord(raw.score)) {
        const s = {};
        const xKey = pickString(raw.score.xKey);
        if (xKey)
            s.xKey = xKey;
        const yKey = pickString(raw.score.yKey);
        if (yKey)
            s.yKey = yKey;
        const xLabel = pickString(raw.score.xLabel);
        if (xLabel)
            s.xLabel = xLabel;
        const yLabel = pickString(raw.score.yLabel);
        if (yLabel)
            s.yLabel = yLabel;
        // reviewer follow-up ⚠️-3: 値域 4 field を pickNumber。
        const xMin = pickNumber(raw.score.xMin);
        if (xMin !== undefined)
            s.xMin = xMin;
        const xMax = pickNumber(raw.score.xMax);
        if (xMax !== undefined)
            s.xMax = xMax;
        const yMin = pickNumber(raw.score.yMin);
        if (yMin !== undefined)
            s.yMin = yMin;
        const yMax = pickNumber(raw.score.yMax);
        if (yMax !== undefined)
            s.yMax = yMax;
        if (isRecord(raw.score.quadrantColors)) {
            const qc = {};
            const tl = pickString(raw.score.quadrantColors.topLeft);
            if (tl)
                qc.topLeft = tl;
            const tr = pickString(raw.score.quadrantColors.topRight);
            if (tr)
                qc.topRight = tr;
            const bl = pickString(raw.score.quadrantColors.bottomLeft);
            if (bl)
                qc.bottomLeft = bl;
            const br = pickString(raw.score.quadrantColors.bottomRight);
            if (br)
                qc.bottomRight = br;
            if (Object.keys(qc).length > 0)
                s.quadrantColors = qc;
        }
        if (Object.keys(s).length > 0)
            out.score = s;
    }
    /* network */
    if (isRecord(raw.network)) {
        const n = {};
        const forceStrength = pickNumber(raw.network.forceStrength);
        if (forceStrength !== undefined)
            n.forceStrength = forceStrength;
        const similarityThreshold = pickNumber(raw.network.similarityThreshold);
        if (similarityThreshold !== undefined)
            n.similarityThreshold = similarityThreshold;
        const linkWeightField = pickString(raw.network.linkWeightField);
        if (linkWeightField)
            n.linkWeightField = linkWeightField;
        if (Object.keys(n).length > 0)
            out.network = n;
    }
    /* postDetailFields */
    const postDetailFields = parsePostDetailFields(raw.postDetailFields);
    if (postDetailFields)
        out.postDetailFields = postDetailFields;
    /* postDetailScores (docs/105 A-③) */
    const postDetailScores = parseScoreDisplayConfig(raw.postDetailScores);
    if (postDetailScores)
        out.postDetailScores = postDetailScores;
    /* aiDialogueSettingsId */
    const aiDialogueSettingsId = pickString(raw.aiDialogueSettingsId);
    if (aiDialogueSettingsId)
        out.aiDialogueSettingsId = aiDialogueSettingsId;
    return out;
}
//# sourceMappingURL=analysisDisplayConfig.js.map